/**
 * The keys a form component can hold a document under. Every one of them is attached freshly
 * on each load — from `getImages`, from a document upload handler, or from resolving a
 * `resource` string — so a persisted copy of any of them is stale by definition: it is either
 * the shape a previous version of the app wrote, or a half-populated component from a tab that
 * closed mid-load, and the object urls inside it died with the session that created them.
 */
const cachedDocumentKeys = [
  'document',
  'documents',
  'images',
  'photos',
  'uploadedDocuments',
]

/**
 * Only a schema component can carry these keys. A stored answer is keyed on a component's own
 * identifier, and nothing stops a form having a field called `images` — so the keys are dropped
 * where they mean a document and left alone everywhere else.
 */
const isSchemaComponent = (value: Record<string, unknown>): boolean =>
  typeof value.component === 'string' && typeof value.identifier === 'string'

/**
 * Drop every document-shaped key from a hydrated cached form, so that whatever renders it
 * fetches the documents again rather than binding a shape — or a dead `blob:` url — from the
 * session before.
 *
 * The walk is by structure rather than by path: a cached form holds its components under
 * `template.schema`, a permit holds them in a flat array, and supplemental forms nest, so the
 * one function reaches all three without knowing which it was handed.
 */
export default function stripCachedDocuments(value: unknown): void {
  if (Array.isArray(value)) {
    value.forEach(stripCachedDocuments)

    return
  }

  if (value === null || typeof value !== 'object') {
    return
  }

  const record = value as Record<string, unknown>

  if (isSchemaComponent(record)) {
    cachedDocumentKeys.forEach((key) => {
      delete record[key]
    })
  }

  Object.values(record).forEach(stripCachedDocuments)
}
